import { debounce } from "lodash";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { AxiosResponse } from "axios";
import { DateTime } from "luxon";
import { useSearchParams } from "react-router-dom";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import {
  NoDataContainer,
  pageSize,
  paginationLabel,
  rowsPerPageLabel,
  ROWS_PER_PAGE_OPTIONS,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
  TableContainerWrapper,
  TablePaginationStyle,
} from "../../Common/styles/table";
import { useTranslation } from "react-i18next";

const SENDGRID = "sendgrid";

type Row = {
  id: string;
  name: string;
  providerTemplateId: string;
  lastSyncedAt: string | null;
};

type Props = {
  canSync: boolean;
};

const EmailTemplatesList = ({ canSync }: Props) => {
  const { t } = useTranslation("email-templates");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [toggle, setToggle] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("query") ?? "");
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) {
      params.set("query", search);
    }
    setSearchParams(params, { replace: true });
  }, [setSearchParams, search]);

  const modifySearchTerm = useMemo(
    () => debounce((val: string) => {
      setSearch(val);
      setPage(0);
    }, 500),
    [],
  );

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(
          `/admin/provider-email-templates`,
          {
            params: {
              provider: SENDGRID,
              page: page + 1,
              size: rowsPerPage,
              ...(search.trim() ? { search: search.trim() } : {}),
            },
          },
        );
        const payload = res?.data?.data;
        const data = (payload?.data ?? []) as Row[];
        setRows(
          data.map((r) => ({
            id: r.id,
            name: r.name ?? "",
            providerTemplateId: r.providerTemplateId ?? "",
            lastSyncedAt: r.lastSyncedAt ? DateTime.fromISO(r.lastSyncedAt).toFormat("dd/MM/yyyy hh:mm a") : null,
          })),
        );
        setTotal(payload?.meta?.total ?? 0);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [page, rowsPerPage, search, toggle]);

  const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res: AxiosResponse = await http.post(
        `/admin/provider-email-templates/sync/sendgrid`,
      );
      toastMessage("success", res?.data?.message);
      setToggle((x) => !x);
      setPage(0);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          width: "100%",
          mb: 2,
        }}
      >
        <TextField
          size="small"
          placeholder={t("SEARCH_PLACEHOLDER")}
          defaultValue={search}
          onChange={(e) => modifySearchTerm(e.target.value)}
          sx={{ width: "70%", minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
        {canSync && (
          <Button
            variant="contained"
            onClick={() => void handleSync()}
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ ml: "auto" }}
          >
            {syncing ? t("SYNCING") : t("SYNC_BUTTON")}
          </Button>
        )}
      </Box>

      <TableContainerWrapper>
        <Box sx={{ overflowX: "auto" }}>
          <Table sx={TableBorderRadiusTopLeftRight}>
            <TableHead>
              <TableRow>
                <StyledTableCell>{t("COLUMN_NAME")}</StyledTableCell>
                <StyledTableCell>{t("COLUMN_TEMPLATE_ID")}</StyledTableCell>
                <StyledTableCell>{t("COLUMN_LAST_SYNCED")}</StyledTableCell>
              </TableRow>
            </TableHead>
            {!loading && rows.length > 0 && (
              <>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <StyledTableCell>{r.name || "-"}</StyledTableCell>
                      <StyledTableCell>{r.providerTemplateId || "-"}</StyledTableCell>
                      <StyledTableCell>
                        {r.lastSyncedAt || "-"}
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <StyledTableCell colSpan={3} sx={{ borderBottom: "none" }}>
                      <TablePagination
                        sx={TablePaginationStyle}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        component="div"
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        labelDisplayedRows={paginationLabel}
                        labelRowsPerPage={rowsPerPageLabel}
                      />
                    </StyledTableCell>
                  </TableRow>
                </TableFooter>
              </>
            )}
          </Table>
        </Box>
        {!loading && rows.length === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              {t("NO_DATA")}
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </TableContainerWrapper>
    </Box>
  );
};

export default EmailTemplatesList;
